import { IconArrowRight } from '@tabler/icons-react'
import { useEffect, useRef, useState } from 'react'
import {
  useLearningStore,
  useDirectLearningStore,
} from '../../store'
import styles from '../../styles/user/LearningContainer.module.scss'
import FractionPreviewText from '../common/FractionPreviewText'
import Loading from '../common/Loading'

const API_ORIGIN = (process.env.REACT_APP_API_URL || '').replace(/\/api$/, '') || 'http://localhost:30022'
const toImgSrc = (url) => url || ''

const LearningQuizStep = ({
  onQuizComplete,
  onQuizDirectComplete,
  quizDirectList,
  onExplanationUpdate,
}) => {
  const userAnswers = useLearningStore((state) => state.userAnswers)

  const {
    questions,
    currentQuestionIndex,
    getCurrentQuestion,
    goToNextQuestion,
    submitAnswer,
    setCurrentQuestionIndex,
  } = useLearningStore()

  // ── 모든 hooks를 최상단에 선언 (조건부 return 전) ──
  const [selectedAnswer, setSelectedAnswer] = useState(null)
  const [submitted, setSubmitted] = useState(null)

  // Math Notes state
  const [showMathNotes, setShowMathNotes] = useState(false)
  const [mathNoteTab, setMathNoteTab] = useState('note')
  const [noteText, setNoteText] = useState('')
  const [drawMode, setDrawMode] = useState('pen')
  const canvasRef = useRef(null)
  const isDrawingRef = useRef(false)
  const lastPosRef = useRef(null)

  const { insertDirectQuizData } = useDirectLearningStore()

  const currentQuestion = getCurrentQuestion()

  // 다음 문제 이미지 preload
  useEffect(() => {
    const nextIndex = currentQuestionIndex + 1
    if (!questions || nextIndex >= questions.length) return
    const next = questions[nextIndex]
    ;[next?.IMG_URL, next?.IMG_URL2].filter(Boolean).forEach((url) => {
      const img = new Image()
      img.src = toImgSrc(url)
    })
  }, [currentQuestionIndex, questions])

  // 문제 변경 시 상태 초기화 (노트 내용도 함께 초기화)
  useEffect(() => {
    setSelectedAnswer(null)
    setSubmitted(null)
    setNoteText('')
    setDrawMode('pen')
    const canvas = canvasRef.current
    if (canvas) {
      const ctx = canvas.getContext('2d')
      ctx.clearRect(0, 0, canvas.width, canvas.height)
    }
  }, [currentQuestionIndex])

  // Draw 탭이 열릴 때 캔버스 픽셀 크기 초기화
  useEffect(() => {
    if (mathNoteTab !== 'draw' || !showMathNotes) return
    const timer = setTimeout(() => {
      const canvas = canvasRef.current
      if (!canvas) return
      const rect = canvas.getBoundingClientRect()
      const w = Math.round(rect.width)
      const h = Math.round(rect.height)
      if (w && h && (canvas.width !== w || canvas.height !== h)) {
        // 기존 내용 보존
        const tmp = document.createElement('canvas')
        tmp.width = canvas.width
        tmp.height = canvas.height
        tmp.getContext('2d').drawImage(canvas, 0, 0)
        canvas.width = w
        canvas.height = h
        canvas.getContext('2d').drawImage(tmp, 0, 0)
      }
    }, 50)
    return () => clearTimeout(timer)
  }, [mathNoteTab, showMathNotes])

  // 문제 변경 시 해설 패널 업데이트 (기존 답 있으면 로드)
  useEffect(() => {
    if (!currentQuestion) return

    const serverAns = quizDirectList?.data?.find(
      (q) => q.QUESTION_ID === currentQuestion.QUESTION_ID
    )
    const storeAns = userAnswers?.find(
      (a) => a.questionId === currentQuestion.QUESTION_ID
    )

    if (!serverAns && !storeAns) {
      onExplanationUpdate?.(null)
      return
    }

    const selIdx = serverAns?.ANSWER ?? storeAns?.selectedAnswerIndex

    const answersLocal = [
      { text: currentQuestion.ANSWER1, isCorrect: currentQuestion.IS_CORRECT_1 === 1 },
      { text: currentQuestion.ANSWER2, isCorrect: currentQuestion.IS_CORRECT_2 === 1 },
      { text: currentQuestion.ANSWER3, isCorrect: currentQuestion.IS_CORRECT_3 === 1 },
      { text: currentQuestion.ANSWER4, isCorrect: currentQuestion.IS_CORRECT_4 === 1 },
      { text: currentQuestion.ANSWER5, isCorrect: currentQuestion.IS_CORRECT_5 === 1 },
    ].filter((a) => a.text && a.text.trim() !== '')

    const corrIdx = answersLocal.findIndex((a) => a.isCorrect)
    const isCorr = serverAns ? serverAns.IS_CORRECT === 'Y' : storeAns?.isCorrect ?? false

    onExplanationUpdate?.({
      isCorrect: isCorr,
      desc: currentQuestion.DESC,
      descUrl: currentQuestion.DESC_URL,
      correctAnswer: corrIdx + 1,
      correctAnswerIndex: corrIdx,
      selectedAnswerIndex: selIdx,
    })
  }, [currentQuestionIndex, quizDirectList, userAnswers])

  // currentQuestion이 없으면 로딩 표시 (hooks 이후에 처리)
  if (!currentQuestion) {
    return (
      <div className={styles.quizStepInner}>
        <Loading />
      </div>
    )
  }

  const answers = [
    { text: currentQuestion.ANSWER1, isCorrect: currentQuestion.IS_CORRECT_1 === 1 },
    { text: currentQuestion.ANSWER2, isCorrect: currentQuestion.IS_CORRECT_2 === 1 },
    { text: currentQuestion.ANSWER3, isCorrect: currentQuestion.IS_CORRECT_3 === 1 },
    { text: currentQuestion.ANSWER4, isCorrect: currentQuestion.IS_CORRECT_4 === 1 },
    { text: currentQuestion.ANSWER5, isCorrect: currentQuestion.IS_CORRECT_5 === 1 },
  ].filter((answer) => answer.text && answer.text.trim() !== '')

  const isImgUrl = (t) => { const s = String(t || '').trim(); return s.startsWith('/') || s.startsWith('http') || /\.(png|jpe?g|gif|webp|svg)$/i.test(s) }
  const hasImageAnswers = answers.some((a) => isImgUrl(a.text))
  const hasLongAnswers = answers.some(
    (a) => !isImgUrl(a.text) && String(a.text || '').length > 10
  )
  const hasVeryShortAnswers = !hasImageAnswers && answers.every(
    (a) => String(a.text || '').length <= 8
  )

  // 서버/스토어 기존 답변 조회
  const answeredFromServer = quizDirectList?.data?.find(
    (q) => q.QUESTION_ID === currentQuestion.QUESTION_ID
  )
  const answeredFromStore = userAnswers?.find(
    (answer) => answer.questionId === currentQuestion.QUESTION_ID
  )
  const isAlreadyAnswered = !!(answeredFromServer || answeredFromStore)

  const existingSelectedIndex = answeredFromServer?.ANSWER ?? answeredFromStore?.selectedAnswerIndex
  const existingIsCorrect = answeredFromServer
    ? answeredFromServer.IS_CORRECT === 'Y'
    : answeredFromStore?.isCorrect ?? false
  const correctAnswerIndex = answers.findIndex((a) => a.isCorrect)

  const existingFeedback = isAlreadyAnswered
    ? {
        isCorrect: existingIsCorrect,
        selectedAnswerIndex: existingSelectedIndex,
        correctAnswerIndex,
        correctAnswer: correctAnswerIndex + 1,
      }
    : null

  // 활성 피드백: 이번 세션 제출 > 기존 답변
  const activeFeedback = submitted ?? existingFeedback
  const isSubmitted = !!activeFeedback

  // Submit Answer 클릭
  const handleSubmit = async () => {
    if (selectedAnswer === null) return

    const selectedAnswerData = answers[selectedAnswer]
    const corrIdx = answers.findIndex((a) => a.isCorrect)
    const correctAnswer = corrIdx !== -1 ? corrIdx + 1 : ''

    // 1. 스토어에 저장
    submitAnswer(currentQuestion.QUESTION_ID, selectedAnswer, selectedAnswerData.isCorrect)

    // 2. Direct 스토어에 데이터 준비
    insertDirectQuizData('questionId', currentQuestion.QUESTION_ID)
    insertDirectQuizData('answer', selectedAnswer)
    insertDirectQuizData('isCorrect', selectedAnswerData.isCorrect)

    // 3. 인라인 피드백 표시
    const feedbackData = {
      isCorrect: selectedAnswerData.isCorrect,
      selectedAnswerIndex: selectedAnswer,
      correctAnswerIndex: corrIdx,
      correctAnswer,
    }
    setSubmitted(feedbackData)

    // 4. 해설 패널 업데이트
    onExplanationUpdate?.({
      isCorrect: selectedAnswerData.isCorrect,
      desc: currentQuestion.DESC,
      descUrl: currentQuestion.DESC_URL,
      correctAnswer,
      correctAnswerIndex: corrIdx,
      selectedAnswerIndex: selectedAnswer,
    })

    // 5. 서버 저장 (백그라운드)
    onQuizDirectComplete().catch((error) => {
      console.error('Failed to save direct quiz data:', error)
    })
  }

  // Next → 클릭
  const handleGoNext = () => {
    const { userAnswers: currentAnswers } = useLearningStore.getState()

    const allAnswered = questions.every((question) =>
      currentAnswers.some((answer) => answer.questionId === question.QUESTION_ID)
    )

    if (allAnswered) {
      onQuizComplete()
      return
    }

    const nextUnansweredIndex = questions.findIndex((question) =>
      !currentAnswers.some((answer) => answer.questionId === question.QUESTION_ID)
    )

    if (nextUnansweredIndex !== -1) {
      setCurrentQuestionIndex(nextUnansweredIndex)
    } else {
      goToNextQuestion()
    }
  }

  // ── Canvas 드로잉 ──
  const getCanvasPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect()
    if (e.touches && e.touches[0]) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top }
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top }
  }

  const startDraw = (e) => {
    isDrawingRef.current = true
    lastPosRef.current = getCanvasPos(e, canvasRef.current)
  }

  const draw = (e) => {
    if (!isDrawingRef.current) return
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    const pos = getCanvasPos(e, canvas)
    ctx.beginPath()
    ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y)
    ctx.lineTo(pos.x, pos.y)
    ctx.lineWidth = drawMode === 'eraser' ? 18 : 2.5
    ctx.strokeStyle = drawMode === 'eraser' ? '#FFFFFF' : '#1E3A5F'
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.stroke()
    lastPosRef.current = pos
  }

  const endDraw = () => {
    isDrawingRef.current = false
    lastPosRef.current = null
  }

  const clearCanvas = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height)
  }

  // ← Previous 클릭
  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1)
    }
  }

  // 답변 버튼 클래스 계산
  const getAnswerBtnClass = (index) => {
    const classes = [styles.answerCircleBtn]

    if (isSubmitted) {
      if (index === activeFeedback.selectedAnswerIndex && activeFeedback.isCorrect) {
        classes.push(styles.btnCorrect)
      } else if (index === activeFeedback.selectedAnswerIndex && !activeFeedback.isCorrect) {
        classes.push(styles.btnWrong)
      } else if (index === activeFeedback.correctAnswerIndex && !activeFeedback.isCorrect) {
        classes.push(styles.btnCorrectHighlight)
      }
    } else {
      if (index === selectedAnswer) {
        classes.push(styles.btnSelected)
      }
    }

    if (hasImageAnswers) classes.push(styles.imageItem)

    return classes.join(' ')
  }

  const hasQuestionImage = !!(currentQuestion.IMG_URL || currentQuestion.IMG_URL2)
  const useSideBySide = hasQuestionImage && !hasImageAnswers

  // 답변 옵션 레이아웃 클래스
  const getAnswerGridClass = () => {
    if (hasImageAnswers) return `${styles.answerOptionsGrid} ${styles.imageGrid}`
    // 좌우 배치일 때는 항상 세로 단일 컬럼
    if (useSideBySide) return styles.answerOptionsGrid
    if (hasLongAnswers) return styles.answerOptionsGrid
    if (hasVeryShortAnswers) return `${styles.answerOptionsGrid} ${styles.oneRow}`
    return `${styles.answerOptionsGrid} ${styles.twoCol}`
  }

  const answersGrid = (
    <div className={getAnswerGridClass()}>
      {answers.map((answer, index) => {
        const isWrongSelected =
          isSubmitted &&
          index === activeFeedback.selectedAnswerIndex &&
          !activeFeedback.isCorrect

        return (
          <button
            key={index}
            className={getAnswerBtnClass(index)}
            onClick={() => !isSubmitted && setSelectedAnswer(index)}
            disabled={isSubmitted}
          >
            <span className={styles.answerNumCircle}>{index + 1}</span>
            <span className={styles.answerCircleText}>
              {isImgUrl(answer.text) ? (
                <img
                  src={toImgSrc(answer.text)}
                  alt={`Answer ${index + 1}`}
                  className={styles.answerImgOption}
                />
              ) : (
                <FractionPreviewText text={answer.text} />
              )}
            </span>
          </button>
        )
      })}
    </div>
  )

  return (
    <div className={styles.quizStepInner}>
      {/* 문제 본문 */}
      <div className={styles.questionTitleCard}>
        <span className={styles.questionNum}>Q{currentQuestionIndex + 1}</span>
        <FractionPreviewText text={currentQuestion.TITLE} />
      </div>

      {/* 이미지+텍스트 보기 → 좌우 배치 */}
      {useSideBySide ? (
        <div className={styles.imageAnswerLayout}>
          <div className={styles.imageAnswerLeft}>
            {currentQuestion.IMG_URL && (
              <img
                src={toImgSrc(currentQuestion.IMG_URL)}
                alt="Question Image"
                className={styles.questionImgSide}
              />
            )}
            {currentQuestion.IMG_URL2 && (
              <img
                src={toImgSrc(currentQuestion.IMG_URL2)}
                alt="Question Image 2"
                className={styles.questionImgSide}
              />
            )}
          </div>
          <div className={styles.imageAnswerRight}>
            {answersGrid}
          </div>
        </div>
      ) : (
        <>
          {/* 문제 이미지 (보기 이미지 케이스 — 세로 유지) */}
          {hasQuestionImage && (
            <div className={styles.questionImagesSection}>
              {currentQuestion.IMG_URL && (
                <img
                  src={toImgSrc(currentQuestion.IMG_URL)}
                  alt="Question Image"
                  className={styles.questionImgFull}
                />
              )}
              {currentQuestion.IMG_URL2 && (
                <img
                  src={toImgSrc(currentQuestion.IMG_URL2)}
                  alt="Question Image 2"
                  className={styles.questionImgFull}
                />
              )}
            </div>
          )}
          {answersGrid}
        </>
      )}

      {/* 인라인 피드백 카드 */}
      {isSubmitted && (
        <div
          className={`${styles.feedbackCard} ${
            activeFeedback.isCorrect ? styles.feedbackCardCorrect : styles.feedbackCardWrong
          }`}
        >
          <div className={styles.feedbackCardLeft}>
            <span
              className={`${styles.feedbackCardIconCircle} ${
                activeFeedback.isCorrect ? styles.iconCorrect : styles.iconWrong
              }`}
            >
              {activeFeedback.isCorrect ? '✓' : '✗'}
            </span>
            <div>
              <div className={styles.feedbackCardTitle}>
                {activeFeedback.isCorrect ? 'Correct!' : 'Incorrect'}
              </div>
              <div className={styles.feedbackCardSub}>
                {activeFeedback.isCorrect
                  ? 'Great job! Keep it up.'
                  : 'Your answer is incorrect.'}
              </div>
            </div>
          </div>

          {!activeFeedback.isCorrect && activeFeedback.correctAnswer && (
            <div className={styles.feedbackCardRight}>
              <span className={styles.feedbackCardCorrectLabel}>Correct Answer</span>
              <div className={styles.feedbackCardCorrectRow}>
                <span className={styles.feedbackCardCorrectNum}>
                  {activeFeedback.correctAnswer}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 하단 네비게이션 */}
      <div className={styles.quizNavigation}>
        <button
          className={styles.prevBtn}
          onClick={handlePrevious}
          disabled={currentQuestionIndex === 0}
        >
          ← Previous
        </button>

        {isSubmitted ? (
          <button className={styles.nextQuizBtn} onClick={handleGoNext}>
            Next <IconArrowRight size={16} /> <img src="/assets/icons/star.png" alt="star" style={{ width: 14, height: 14, objectFit: 'contain', verticalAlign: 'middle' }} />
          </button>
        ) : (
          <button
            className={styles.submitAnswerBtn}
            onClick={handleSubmit}
            disabled={selectedAnswer === null}
          >
            Submit Answer
          </button>
        )}
      </div>

      {/* Math Notes */}
      <div className={styles.mathNotesSection}>
        <div
          className={styles.mathNotesToggleRow}
          onClick={() => setShowMathNotes((v) => !v)}
        >
          <span className={styles.mathNotesHint}>
            {showMathNotes ? <><img src="/assets/icons/note.png" alt="note" style={{ width: 14, height: 14, objectFit: 'contain', verticalAlign: 'middle', marginRight: 4 }} />Math Notes</> : <><img src="/assets/icons/hint.png" alt="hint" style={{ width: 14, height: 14, objectFit: 'contain', verticalAlign: 'middle', marginRight: 4 }} />Use this space to solve the problem.</>}
          </span>
          <button
            className={styles.mathNotesToggleBtn}
            onClick={(e) => { e.stopPropagation(); setShowMathNotes((v) => !v) }}
          >
            {showMathNotes ? 'Hide ▲' : 'Show Math Notes ▼'}
          </button>
        </div>

        {showMathNotes && (
          <div className={styles.mathNotesPanel}>
            {/* 패널 헤더: 타이틀 + 탭 */}
            <div className={styles.mathNotesPanelHeader}>
              <div className={styles.mathNotesTitleGroup}>
                <img src="/assets/icons/note.png" alt="note" className={styles.mathNotesTitleIcon} style={{ width: 18, height: 18, objectFit: 'contain' }} />
                <span className={styles.mathNotesTitleText}>Math Notes</span>
                <span className={styles.mathNotesTitleSub}>Show your work here!</span>
              </div>
              <div className={styles.mathNotesTabs}>
                <button
                  className={`${styles.mathNotesTab} ${mathNoteTab === 'note' ? styles.mathNotesTabActive : ''}`}
                  onClick={() => setMathNoteTab('note')}
                >
                  <img src="/assets/icons/note.png" alt="note" style={{ width: 14, height: 14, objectFit: 'contain', verticalAlign: 'middle', marginRight: 4 }} />Note
                </button>
                <button
                  className={`${styles.mathNotesTab} ${mathNoteTab === 'draw' ? styles.mathNotesTabActive : ''}`}
                  onClick={() => setMathNoteTab('draw')}
                >
                  <img src="/assets/icons/pencil.png" alt="draw" style={{ width: 14, height: 14, objectFit: 'contain', verticalAlign: 'middle', marginRight: 4 }} />Draw
                </button>
              </div>
            </div>

            {/* Note 탭 — 줄 노트 스타일, CSS hidden으로 마운트 유지 */}
            <textarea
              className={`${styles.mathNotesTextarea} ${mathNoteTab !== 'note' ? styles.mathNotesHidden : ''}`}
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Write your thinking here..."
              maxLength={1000}
            />
            {mathNoteTab === 'note' && (
              <div className={styles.mathNotesCounter}>{noteText.length} / 1000</div>
            )}

            {/* Draw 탭 — CSS hidden으로 캔버스 상태 보존 */}
            <div className={`${styles.mathNotesCanvasWrapper} ${mathNoteTab !== 'draw' ? styles.mathNotesHidden : ''}`}>
              <canvas
                ref={canvasRef}
                className={styles.mathNotesCanvas}
                onMouseDown={startDraw}
                onMouseMove={draw}
                onMouseUp={endDraw}
                onMouseLeave={endDraw}
                onTouchStart={startDraw}
                onTouchMove={draw}
                onTouchEnd={endDraw}
              />
              <div className={styles.mathNotesDrawTools}>
                <button
                  className={`${styles.mathNotesDrawBtn} ${drawMode === 'pen' ? styles.mathNotesDrawBtnActive : ''}`}
                  onClick={() => setDrawMode('pen')}
                >
                  <img src="/assets/icons/pencil.png" alt="pen" style={{ width: 14, height: 14, objectFit: 'contain', verticalAlign: 'middle', marginRight: 4 }} />Pen
                </button>
                <button
                  className={`${styles.mathNotesDrawBtn} ${drawMode === 'eraser' ? styles.mathNotesDrawBtnActive : ''}`}
                  onClick={() => setDrawMode('eraser')}
                >
                  🧹 Eraser
                </button>
                <button className={styles.mathNotesClearBtn} onClick={clearCanvas}>
                  Clear All
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default LearningQuizStep
